import { useState, useEffect, useRef, useMemo } from 'react';
import { FieldInfo } from '../models';

interface UsePreviewStateProps {
  activePreviewFieldId: string | null;
  fieldMap: Map<string, FieldInfo>;
  selectors: Record<string, any>;
}

export const usePreviewState = ({
  activePreviewFieldId,
  fieldMap,
  selectors,
}: UsePreviewStateProps) => {
  const [previewSelectorInput, setPreviewSelectorInput] = useState('');
  const [previewSelectorAllInput, setPreviewSelectorAllInput] = useState('');
  const [previewSelectorError, setPreviewSelectorError] = useState<string | null>(null);
  const [previewSelectorAllError, setPreviewSelectorAllError] = useState<string | null>(null);

  const previewInputsRef = useRef({ single: '', all: '' });
  const lastSyncedSelectorRef = useRef('');
  const lastSyncedFieldRef = useRef<string | null>(null);

  // Update refs when inputs change
  useEffect(() => {
    previewInputsRef.current = {
      single: previewSelectorInput,
      all: previewSelectorAllInput,
    };
  }, [previewSelectorInput, previewSelectorAllInput]);

  // Reset errors when active field changes
  useEffect(() => {
    lastSyncedSelectorRef.current = '';
    setPreviewSelectorError(null);
    setPreviewSelectorAllError(null);
  }, [activePreviewFieldId]);

  // Sync selector inputs with active field
  useEffect(() => {
    if (!activePreviewFieldId) {
      return;
    }

    const field = fieldMap.get(activePreviewFieldId);
    const selectorConfig = selectors[activePreviewFieldId];
    const normalizedSelector =
      selectorConfig?.selector === '__PLACEHOLDER__'
        ? ''
        : selectorConfig?.selector || '';
    const { single, all } = previewInputsRef.current;
    const lastSynced = lastSyncedSelectorRef.current;
    const isNewField = lastSyncedFieldRef.current !== activePreviewFieldId;

    if (!normalizedSelector) {
      lastSyncedSelectorRef.current = '';
      lastSyncedFieldRef.current = activePreviewFieldId;
      if (single || all) {
        setPreviewSelectorInput('');
        setPreviewSelectorAllInput('');
      }
      return;
    }

    if (field?.multiple) {
      if (isNewField || !all || all === lastSynced) {
        setPreviewSelectorAllInput(normalizedSelector);
      }
      if (isNewField || single === lastSynced) {
        setPreviewSelectorInput('');
      }
    } else {
      if (isNewField || !single || single === lastSynced) {
        setPreviewSelectorInput(normalizedSelector);
      }
      if (isNewField || (all && all === lastSynced)) {
        setPreviewSelectorAllInput('');
      }
    }

    lastSyncedSelectorRef.current = normalizedSelector;
    lastSyncedFieldRef.current = activePreviewFieldId;
  }, [activePreviewFieldId, fieldMap, selectors]);

  const activePreviewField = activePreviewFieldId
    ? fieldMap.get(activePreviewFieldId) || null
    : null;

  const previewSinglePlaceholder = useMemo(() => {
    if (!activePreviewField) {
      return undefined;
    }
    if (activePreviewField.complex) {
      return `e.g., .${activePreviewField.id}-container`;
    }
    if (activePreviewField.multiple) {
      return `e.g., .${activePreviewField.id}-item`;
    }
    return `e.g., .${activePreviewField.id}`;
  }, [activePreviewField]);

  const previewMultiPlaceholder = useMemo(() => {
    if (!activePreviewField) {
      return undefined;
    }
    return `e.g., .${activePreviewField.id}-item`;
  }, [activePreviewField]);

  return {
    previewSelectorInput,
    setPreviewSelectorInput,
    previewSelectorAllInput,
    setPreviewSelectorAllInput,
    previewSelectorError,
    setPreviewSelectorError,
    previewSelectorAllError,
    setPreviewSelectorAllError,
    activePreviewField,
    previewSinglePlaceholder,
    previewMultiPlaceholder,
  };
};
